/**
 * Phase 54.24 — Tiny in-memory GET cache for stable reference data.
 *
 * Motivation: /branches, /categories, /accounting/brands are refetched
 * on EVERY inventory tab switch (silver ↔ gold ↔ watch). Each request
 * is ~120 ms warm. Caching them with a 5-minute TTL eliminates 3-4
 * requests per switch → tab open feels instant.
 *
 * Contract:
 *   - Only safe UI data (categories, columns, filter metadata, branch
 *     metadata) — never inventory quantities, prices, rates.
 *   - Zero disk / localStorage / cookies — in-memory only. Full reload
 *     clears the cache (fresh data on every session).
 *   - Concurrent callers for the same key share the same in-flight
 *     promise — dedupes a burst of parallel fetches into a single one.
 *   - `invalidate(key)` explicit — CRUD operations that mutate ref data
 *     (e.g. add a branch, edit a brand) MUST call it to drop the stale
 *     entry.
 */
const store = new Map();       // key → { data, expires }
const inflight = new Map();    // key → Promise<data>

const DEFAULT_TTL = 5 * 60 * 1000;  // 5 minutes

/**
 * Cached GET.
 * @param {string} key         cache key (usually the URL + query)
 * @param {() => Promise<any>} fetcher  async function returning the data
 * @param {object} [opts]      { ttl }
 */
export async function cachedGet(key, fetcher, opts = {}) {
  const now = Date.now();
  const hit = store.get(key);
  if (hit && hit.expires > now) return hit.data;

  // Coalesce concurrent callers so a burst of tab-mounts fires ONE
  // network round-trip instead of N.
  if (inflight.has(key)) return inflight.get(key);

  const ttl = typeof opts.ttl === "number" ? opts.ttl : DEFAULT_TTL;
  const promise = (async () => {
    try {
      const data = await fetcher();
      store.set(key, { data, expires: Date.now() + ttl });
      return data;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, promise);
  return promise;
}

/** Drop a cached entry (e.g. after a create/edit/delete on that resource). */
export function invalidate(key) {
  store.delete(key);
  inflight.delete(key);
}

/** Drop every cached entry whose key contains `substr`. */
export function invalidateMatching(substr) {
  for (const k of store.keys()) if (k.includes(substr)) store.delete(k);
  for (const k of inflight.keys()) if (k.includes(substr)) inflight.delete(k);
}

/** Testing / diagnostics only. */
export function _debugState() {
  return { entries: store.size, inflight: inflight.size, keys: [...store.keys()] };
}
